import { createTheme } from "@mui/material/styles";

// Original Wakaba green, paired only with Tailwind Stone neutrals.
export const siteColors = {
  greenMain: "#55735B",
  greenLight: "#E8F0E8",
  greenDark: "#37503D",
  stone50: "#FAFAF9",
  stone100: "#F5F5F4",
  stone200: "#E7E5E4",
  stone500: "#78716C",
  stone600: "#57534E",
  stone700: "#44403C",
  stone800: "#292524",
  stone900: "#1C1917",
  white: "#FFFFFF",
} as const;

const theme = createTheme({
  palette: {
    primary: {
      main: siteColors.greenMain,
      light: siteColors.greenLight,
      dark: siteColors.greenDark,
      contrastText: siteColors.white,
    },
    secondary: {
      main: siteColors.stone500,
      light: siteColors.stone100,
      dark: siteColors.stone700,
      contrastText: siteColors.white,
    },
    background: {
      default: siteColors.stone50,
      paper: siteColors.white,
    },
    text: {
      primary: siteColors.stone800,
      secondary: siteColors.stone600,
    },
    divider: siteColors.stone200,
  },
  shape: {
    borderRadius: 18,
  },
  typography: {
    fontFamily: [
      "Noto Sans JP",
      "Hiragino Sans",
      "Hiragino Kaku Gothic ProN",
      "Yu Gothic",
      "Meiryo",
      "sans-serif",
    ].join(","),
    h1: {
      fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
      fontWeight: 700,
      letterSpacing: "0.02em",
      lineHeight: 1.35,
    },
    h2: {
      fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
      fontWeight: 700,
      letterSpacing: "0.025em",
      lineHeight: 1.4,
    },
    h3: {
      fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
      fontWeight: 700,
      lineHeight: 1.45,
    },
    h4: {
      fontWeight: 700,
      lineHeight: 1.45,
    },
    h5: {
      fontWeight: 700,
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 700,
      lineHeight: 1.55,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.95,
    },
    body2: {
      lineHeight: 1.8,
    },
    button: {
      fontWeight: 700,
      letterSpacing: "0.02em",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { scrollBehavior: "smooth" },
        body: {
          backgroundColor: siteColors.stone50,
          overflowX: "hidden",
        },
        "::selection": {
          backgroundColor: siteColors.greenLight,
          color: siteColors.greenDark,
        },
        a: { textUnderlineOffset: "0.2em" },
        img: { display: "block" },
        "*:focus-visible": {
          outline: `3px solid ${siteColors.greenMain}`,
          outlineOffset: "3px",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 999,
          minHeight: 48,
          paddingInline: 24,
          textTransform: "none",
        },
        sizeLarge: {
          minHeight: 56,
          paddingInline: 30,
          fontSize: "1rem",
        },
        textPrimary: {
          color: siteColors.greenDark,
        },
        outlinedPrimary: {
          color: siteColors.greenDark,
          borderColor: siteColors.greenMain,
          "&:hover": {
            borderColor: siteColors.greenDark,
            backgroundColor: siteColors.greenLight,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          backgroundImage: "none",
        },
        elevation3: {
          border: `1px solid ${siteColors.stone200}`,
          boxShadow: "0 16px 50px rgba(41, 37, 36, 0.08)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          border: `1px solid ${siteColors.stone200}`,
          boxShadow: "0 16px 50px rgba(41, 37, 36, 0.08)",
          overflow: "hidden",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: siteColors.stone200,
          paddingTop: 16,
          paddingBottom: 16,
        },
        head: {
          color: siteColors.greenDark,
          fontWeight: 700,
        },
      },
    },
  },
});

export default theme;
